import { Monitor, Moon, Sun } from 'lucide-react'
import type { ThemePreference } from '@bre-hub/contracts'
import { useMe } from '../session'
import { THEME_OPTIONS, themeOrDefault, useSetTheme } from '../theme'
import { ErrorNotice } from './ui'

const ICONS: Record<ThemePreference, typeof Sun> = { light: Sun, dark: Moon, match: Monitor }

// The real BRE logo (public/brand/bre-logo.svg, from bre.live), drawn as a
// mask so one file takes the theme's logo color.
export function BrandLogo({ size = 'sidebar' }: { size?: 'sidebar' | 'page' }) {
  return <span className={`brand-logo brand-logo-${size}`} role="img" aria-label="Burlington Riverfront Entertainment" />
}

// Light, dark or match the device (ADR 0012). Compact in the sidebar, with
// each option explained in My settings. Saved on your Hub record, so it
// follows you to every device.
export function ThemeSwitch({ variant }: { variant: 'compact' | 'full' }) {
  const current = themeOrDefault(useMe().theme)
  const save = useSetTheme(current)

  if (variant === 'compact') {
    return (
      <div className="theme-switch" role="radiogroup" aria-label="Theme">
        {THEME_OPTIONS.map((option) => {
          const Icon = ICONS[option.value]
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={current === option.value}
              title={option.label}
              className="theme-switch-option"
              onClick={() => current !== option.value && save.mutate(option.value)}
            >
              <Icon size={14} aria-hidden />
              {option.short}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <fieldset className="theme-choices">
      <legend className="sr-only">Theme</legend>
      {THEME_OPTIONS.map((option) => {
        const Icon = ICONS[option.value]
        return (
          <label key={option.value} className="theme-choice">
            <input type="radio" name="theme" value={option.value} checked={current === option.value} onChange={() => save.mutate(option.value)} />
            <Icon size={17} aria-hidden />
            <span>
              <strong>{option.label}</strong>
              <span className="faint theme-choice-desc">{option.description}</span>
            </span>
          </label>
        )
      })}
      {save.error && <ErrorNotice error={save.error} />}
    </fieldset>
  )
}
